import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormControl } from '@/components/ui/form';

const NONE_VALUE = '__none__';

export interface SelectOption {
  value: string;
  label: string;
}

interface SimpleSelectProps {
  options: SelectOption[];
  value?: string;
  onValueChange: (value: string | undefined) => void;
  placeholder?: string;
  noneLabel?: string;
  disabled?: boolean;
  className?: string;
  formControl?: boolean;
  ariaLabel?: string;
  /** `sm` renders the compact list-filter trigger shape (see SelectTrigger). */
  size?: 'default' | 'sm';
  /** Focus the trigger on mount (e.g. the first field left to fill). */
  autoFocus?: boolean;
}

export function SimpleSelect({
  options,
  value,
  onValueChange,
  placeholder,
  noneLabel,
  disabled,
  className,
  formControl = false,
  ariaLabel,
  size = 'default',
  autoFocus,
}: SimpleSelectProps) {
  const internalValue = noneLabel
    ? (value ?? NONE_VALUE)
    : (value ?? undefined);

  const handleChange = (val: string) => {
    if (noneLabel && val === NONE_VALUE) {
      onValueChange(undefined);
    } else {
      onValueChange(val);
    }
  };

  const trigger = (
    <SelectTrigger
      className={className}
      size={size}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  return (
    <Select
      value={internalValue}
      onValueChange={handleChange}
      disabled={disabled}
    >
      {formControl ? <FormControl>{trigger}</FormControl> : trigger}
      <SelectContent>
        {noneLabel && <SelectItem value={NONE_VALUE}>{noneLabel}</SelectItem>}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
